import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, TrendingUp, ShoppingBag, Users, Gift, Banknote, Loader2, Receipt, Info, Coins, Sparkles, Heart, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner, StatCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;
const KIND_LABEL = { character: "Character", game: "World", asset: "Asset" };
const STATUS_COLOR = { requested: "secondary", paid: "default", rejected: "destructive" };
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };

export default function Earnings() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [methodOpen, setMethodOpen] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/marketplace/earnings"); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <Spinner />;

  return (
    <div className="p-5 lg:p-8 max-w-5xl mx-auto" data-testid="earnings-page">
      <PageHeader title="Creator Earnings" icon={Wallet}
        subtitle="Track what your sales and tips have earned and cash out your full balance."
        action={<Button onClick={() => navigate("/app/marketplace")} variant="outline" className="rounded-full">Go to Marketplace</Button>} />

      <TipsWeekBanner />

      {/* Balance hero */}
      <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[#1f6feb]/15 to-transparent p-5 sm:p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Available balance</div>
          <div className="text-4xl font-display font-bold tabular-nums mt-1" data-testid="available-balance">{usd(data.available)}</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
            Net {usd(data.net)} sales after {Math.round(data.fee_rate * 100)}% fee{data.tips > 0 ? ` + ${usd(data.tips)} tips (100%)` : ""} · Min payout {usd(data.min_payout)}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={() => setConvertOpen(true)} disabled={data.available <= 0} variant="outline"
            className="rounded-full px-5" data-testid="convert-credits-btn">
            <Coins className="w-4 h-4" /> Convert to credits
          </Button>
          <Button onClick={() => setPayoutOpen(true)} disabled={data.available < data.min_payout}
            className="rounded-full px-6" data-testid="request-payout-btn">
            <Banknote className="w-4 h-4" /> Cash payout
          </Button>
        </div>
      </div>
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 mb-6 text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-2" data-testid="wallet-rate-note">
        <Coins className="w-4 h-4 text-[hsl(var(--primary))]" /> Wallet: cash out, or convert earnings to AI credits at <b className="text-[hsl(var(--foreground))]">{data.credit_conversion_rate || 100} credits / $1</b> — your call (do half &amp; half if you like!).
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Gross sales" value={usd(data.gross)} icon={TrendingUp} accent="#22c55e" />
        <StatCard label="Paid sales" value={data.total_sales} icon={ShoppingBag} accent="#3b9eff" delay={1} />
        <StatCard label="Unique buyers" value={data.unique_buyers} icon={Users} accent="#a855f7" delay={2} />
        <StatCard label="Free shares" value={data.free_shares} icon={Gift} accent="#f59e0b" delay={3} />
      </div>

      <SupportersHub />

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-3 mb-6 flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <span>GameView keeps a {Math.round(data.fee_rate * 100)}% platform fee on paid sales; tips pass through to you in full. Payout requests are queued and disbursed by the GameView team to your chosen method (PayPal, bank, or Stripe).</span>
      </div>

      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 mb-6 flex items-center justify-between gap-3" data-testid="payout-method-card">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] flex items-center gap-1.5"><Banknote className="w-3.5 h-3.5" /> Payout method</div>
          {data.payout_method?.destination ? (
            <div className="mt-1 text-sm" data-testid="payout-method-value">
              <span className="capitalize font-semibold">{data.payout_method.method}</span>
              <span className="text-[hsl(var(--muted-foreground))]"> · {data.payout_method.destination}</span>
            </div>
          ) : (
            <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]" data-testid="payout-method-empty">No payout method saved yet — add one to cash out faster.</div>
          )}
        </div>
        <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={() => setMethodOpen(true)} data-testid="edit-payout-method-btn">
          {data.payout_method?.destination ? "Edit" : "Set up"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section title="Top earners" icon={TrendingUp}>
          {data.breakdown.length === 0 ? <Empty text="No sales yet — list an item on the Marketplace to start earning." /> : (
            <div className="divide-y divide-[hsl(var(--border))]" data-testid="earnings-breakdown">
              {data.breakdown.slice(0, 8).map((b, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5">
                  <span className="text-xl">{b.emoji || "📦"}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{b.title}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{KIND_LABEL[b.kind]} · {b.sales} sold{b.shared ? " · revenue split" : ""}</div>
                  </div>
                  <div className="font-bold tabular-nums">{usd(b.revenue)}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Recent sales" icon={Receipt}>
          {data.recent.length === 0 ? <Empty text="Your sales activity will show up here." /> : (
            <div className="divide-y divide-[hsl(var(--border))]" data-testid="recent-sales">
              {data.recent.map((s, i) => (
                <div key={i} className="flex items-center gap-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                      {s.title}
                      {s.role === "collaborator" && <Badge variant="outline" className="text-[9px]">Co-creator share</Badge>}
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                      {s.buyer_name || "Someone"} · {fmtDate(s.created_at)}
                      {s.shared && <span className="text-[hsl(var(--primary))]"> · split {s.contributors} ways</span>}
                    </div>
                  </div>
                  <div className={`text-sm font-semibold tabular-nums ${s.price > 0 ? "text-emerald-500" : "text-[hsl(var(--muted-foreground))]"}`}>
                    {s.price > 0 ? usd(s.price) : "Free"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {data.payouts.length > 0 && (
        <Section title="Payout history" icon={Banknote} className="mt-6">
          <div className="divide-y divide-[hsl(var(--border))]" data-testid="payout-history">
            {data.payouts.map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2.5">
                <div className="flex-1">
                  <div className="font-semibold text-sm tabular-nums">{usd(p.amount)}</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] capitalize">{p.method} · {fmtDate(p.created_at)}</div>
                </div>
                <Badge variant={STATUS_COLOR[p.status] || "secondary"} className="capitalize">{p.status}</Badge>
              </div>
            ))}
          </div>
        </Section>
      )}

      <PayoutDialog open={payoutOpen} available={data.available} min={data.min_payout} saved={data.payout_method}
        onClose={() => setPayoutOpen(false)} onDone={() => { setPayoutOpen(false); load(); }} />
      <ConvertDialog open={convertOpen} available={data.available} rate={data.credit_conversion_rate || 100}
        onClose={() => setConvertOpen(false)} onDone={() => { setConvertOpen(false); load(); }} />
      <PayoutMethodDialog open={methodOpen} saved={data.payout_method}
        onClose={() => setMethodOpen(false)} onDone={() => { setMethodOpen(false); load(); }} />
    </div>
  );
}

function ConvertDialog({ open, available, rate, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const credits = Math.round((parseFloat(amount) || 0) * rate);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/marketplace/wallet/convert", { amount: parseFloat(amount) || 0 });
      toast.success(data.message);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Coins className="w-5 h-5 text-[hsl(var(--primary))]" /> Convert earnings to credits</DialogTitle>
          <DialogDescription>Turn your balance into AI credits at {rate} credits per $1. Available: ${Number(available).toFixed(2)}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Amount (USD)</span>
            <Input type="number" min="0" max={available} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="mt-1" data-testid="convert-amount" />
            <div className="flex gap-1.5 mt-1.5">
              <Button size="sm" variant="outline" className="rounded-full text-[11px] h-7" onClick={() => setAmount((available / 2).toFixed(2))} data-testid="convert-half">Half</Button>
              <Button size="sm" variant="outline" className="rounded-full text-[11px] h-7" onClick={() => setAmount(available.toFixed(2))} data-testid="convert-all">All</Button>
            </div>
          </div>
          <div className="rounded-xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/10 p-3 text-center" data-testid="convert-preview">
            <Sparkles className="w-4 h-4 inline text-[hsl(var(--primary))]" /> You'll get <b>{credits.toLocaleString()}</b> AI credits
          </div>
          <Button onClick={submit} disabled={saving || credits <= 0} className="w-full rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="convert-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />} Convert
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function TipsWeekBanner() {
  const navigate = useNavigate();
  const [week, setWeek] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [thankOpen, setThankOpen] = useState(false);
  useEffect(() => {
    api.get("/tips/me/weekly")
      .then((r) => setWeek(r.data?.week || null))
      .catch(() => setWeek(null));
  }, []);
  const weekStamp = Math.floor(Date.now() / WEEK_MS);
  const dismissed = typeof window !== "undefined" && localStorage.getItem("gv_tips_week_dismissed") === String(weekStamp);
  if (!week || week.count <= 0 || hidden || dismissed) return null;
  const dismiss = () => { localStorage.setItem("gv_tips_week_dismissed", String(weekStamp)); setHidden(true); };
  const recent = (week.recent || []).slice(0, 3);
  return (
    <div className="relative rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 to-transparent p-5 mb-6" data-testid="tips-week-banner">
      <button onClick={dismiss} className="absolute top-3 right-3 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="tips-week-dismiss" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-emerald-500/20 shrink-0">
          <Heart className="w-5 h-5 text-emerald-400 fill-emerald-400/40" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg font-bold text-emerald-300">You earned tips this week! 💚</div>
          <div className="text-sm text-[hsl(var(--foreground))] mt-0.5">
            <b className="tabular-nums" data-testid="tips-week-amount">{usd(week.total)}</b> from{" "}
            <b>{week.count}</b> tip{week.count === 1 ? "" : "s"}
            {week.top_game ? <> · most-tipped: <b>{week.top_game}</b></> : null}
          </div>
          {recent.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid="tips-week-supporters">
              {recent.map((t, i) => (
                <Badge key={i} variant="secondary" className="text-[11px] gap-1 max-w-[240px] truncate">
                  <span className="font-semibold">{t.tipper_name}</span>
                  <span className="text-emerald-400 tabular-nums">{usd(t.amount)}</span>
                  {t.message ? <span className="text-[hsl(var(--muted-foreground))] truncate">“{t.message}”</span> : null}
                </Badge>
              ))}
            </div>
          )}
          {week.top_game_id && (
            <Button onClick={() => setThankOpen(true)} size="sm"
              className="rounded-full mt-3 bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="thank-supporters-btn">
              <Heart className="w-3.5 h-3.5" /> Thank your supporters
            </Button>
          )}
        </div>
      </div>
      <ThankSupportersDialog open={thankOpen} week={week} onClose={() => setThankOpen(false)}
        onPosted={(gid) => { setThankOpen(false); navigate(`/app/community/${gid}`); }} />
    </div>
  );
}

function ThankSupportersDialog({ open, week, onClose, onPosted }) {
  const draft = `Huge thanks to everyone who tipped ${week?.top_game || "my game"} this week — your support means the world and keeps me building. 💚`;
  const [message, setMessage] = useState(draft);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setMessage(draft); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/tips/me/thank", { game_id: week.top_game_id, message });
      toast.success(data.already_posted ? "You already posted a thank-you this week 💚" : "Thank-you posted to your Community feed! 💚");
      onPosted(data.game_id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="thank-supporters-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-emerald-400" /> Thank your supporters</DialogTitle>
          <DialogDescription>Post a public thank-you to <b>{week?.top_game}</b>'s Community feed. It shows supporters the love and nudges more tips.</DialogDescription>
        </DialogHeader>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={4000} data-testid="thank-message" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving || !message.trim()} className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="thank-post-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />} Post thank-you
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const Section = ({ title, icon: Icon, children, className = "" }) => (  <div className={`rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 ${className}`}>
    <h2 className="font-bold mb-2 flex items-center gap-2"><Icon className="w-4 h-4 text-[hsl(var(--primary))]" /> {title}</h2>
    {children}
  </div>
);
const Empty = ({ text }) => <div className="text-sm text-[hsl(var(--muted-foreground))] py-4">{text}</div>;

function SupportersHub() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/tips/me/supporters").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d || d.count === 0) return null;
  const medal = ["🥇", "🥈", "🥉"];
  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/[0.07] to-transparent p-5 mb-6" data-testid="supporters-hub">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-display font-bold flex items-center gap-2"><Heart className="w-5 h-5 text-emerald-400 fill-emerald-400" /> Your Supporters</h2>
        <div className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="supporters-hub-total">
          <b className="text-emerald-400">{usd(d.total)}</b> from {d.supporter_count} supporter{d.supporter_count === 1 ? "" : "s"} · {d.count} tip{d.count === 1 ? "" : "s"}
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div data-testid="supporters-list">
          <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Top supporters</div>
          <div className="space-y-2">
            {d.supporters.slice(0, 8).map((s, i) => (
              <div key={s.tipper_id} className="flex items-center gap-2.5 text-sm" data-testid={`supporter-row-${i}`}>
                <span className="w-5 text-center shrink-0">{medal[i] || <span className="text-xs text-[hsl(var(--muted-foreground))]">{i + 1}</span>}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{s.name}{s.count > 1 && <span className="text-[hsl(var(--muted-foreground))] text-xs"> · {s.count} tips</span>}</div>
                  {s.last_message && <div className="text-[11px] text-[hsl(var(--muted-foreground))] italic truncate">“{s.last_message}”</div>}
                </div>
                <span className="font-bold text-emerald-400 tabular-nums shrink-0">{usd(s.total)}</span>
              </div>
            ))}
          </div>
        </div>
        <div data-testid="supporters-by-game">
          <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">By game</div>
          <div className="space-y-2">
            {d.by_game.slice(0, 6).map((g) => (
              <div key={g.game_id} className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate">{g.title}</span>
                <span className="shrink-0 flex items-center gap-1.5">
                  <b className="text-emerald-400 tabular-nums">{usd(g.raised)}</b>
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-0.5"><Users className="w-3 h-3" /> {g.supporters}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PayoutMethodDialog({ open, saved, onClose, onDone }) {
  const [method, setMethod] = useState("paypal");
  const [destination, setDestination] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setMethod(saved?.method || "paypal"); setDestination(saved?.destination || ""); } }, [open, saved]);
  const submit = async () => {
    setSaving(true);
    try {
      await api.put("/marketplace/wallet/payout-method", { method, destination });
      toast.success("Payout method saved!");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="payout-method-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Banknote className="w-5 h-5 text-[hsl(var(--primary))]" /> Payout method</DialogTitle>
          <DialogDescription>Save where you'd like to receive your cash payouts. Used by default on every payout request.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1" data-testid="pm-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="bank">Bank transfer</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Send to (email / account ref)</label>
            <Input value={destination} onChange={(e) => setDestination(e.target.value)} className="mt-1"
              placeholder="you@paypal.com" data-testid="pm-destination" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving || !destination.trim()} className="rounded-full" data-testid="pm-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save method"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PayoutDialog({ open, available, min, saved, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("paypal");
  const [destination, setDestination] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setAmount(String(available)); setMethod(saved?.method || "paypal"); setDestination(saved?.destination || ""); } }, [open, available, saved]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/marketplace/payout", { amount: parseFloat(amount) || 0, method, destination });
      toast.success(data.message);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="payout-dialog">
        <DialogHeader>
          <DialogTitle>Request a payout</DialogTitle>
          <DialogDescription>Cash out your available balance of {usd(available)}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Amount (USD)</label>
            <Input type="number" min={min} step="1" max={available} value={amount} onChange={(e) => setAmount(e.target.value)}
              className="mt-1" data-testid="payout-amount" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Method</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="mt-1" data-testid="payout-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="bank">Bank transfer</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Send to (email / account ref)</label>
            <Input value={destination} onChange={(e) => setDestination(e.target.value)} className="mt-1"
              placeholder="you@paypal.com" data-testid="payout-destination" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full" data-testid="payout-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request payout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
